import { SubstratZeile } from '../../data-model';
import { wechselLagen } from '../detail/wechsel-gruppen';
import { bodenart } from './grubenbild-bodenarten';

/**
 * Was die Substratfolge ueber die Baender eines Bb-Horizontes verraet.
 *
 * Ein Baenderhorizont hat in aller Regel ein Gegenstueck in der Substratfolge:
 * Dort sagt der Wechsel-Haken, WELCHE Substrate sich hier als Baender abloesen
 * (Hinweis des Kartierers, 19.09.2026). Grube 033 (Torgelow) zeigt es: Der
 * Bb-Horizont reicht von 135 bis 300 cm, und die Substratzeilen bei 145, 180
 * und 200 cm bilden eine Wechselkette aus `almfS` (10 cm), `mfS` (35 cm) und
 * `mS` (20 cm).
 *
 * Daraus laesst sich die Baenderung ableiten, statt sie zu wuerfeln: Die
 * Kette nennt die beteiligten Substrate, und ihre Maechtigkeiten geben das
 * Mengenverhaeltnis.
 *
 * ⚠ Was die Daten NICHT hergeben, ist die Zahl der einzelnen Baender: Erfasst
 * ist je Substrat nur die Summe seiner Lagen, nicht, auf wie viele Baender sie
 * sich verteilt. Diese Zahl bleibt eine Darstellungsentscheidung, und sie wird
 * unten offen als solche getroffen.
 */

export interface WechselLage2 {
	substrat: string;
	/** Summe der Maechtigkeit dieses Substrats im Wechselbereich, in cm. */
	maechtigkeit: number;
	/** Anteil an der ganzen Kette, 0 bis 1. */
	anteil: number;
	/** Koernung 0 (Staubsand) bis 5 (Grobsand); bindige Substrate liefern 0. */
	korn: number;
}

export interface Wechselbereich {
	/** Obergrenze in cm. */
	von: number;
	/** Untergrenze in cm. */
	bis: number;
	lagen: WechselLage2[];
	/** Die duennste erfasste Lage in cm - sie begrenzt, wie fein es wird. */
	duennste: number;
}

/**
 * Alle Wechselketten einer Substratfolge.
 *
 * Die Regel, welche Zeilen zu einer Kette gehoeren, steht einmal in
 * `wechsel-gruppen.ts` und wird hier nur gelesen - die Klammer in der Erfassung,
 * im Ausdruck und dieses Bild sollen dieselbe Kette meinen.
 */
export function wechselbereiche(substrate: SubstratZeile[] | undefined | null): Wechselbereich[] {
	const zeilen = substrate ?? [];
	const lagen = wechselLagen(zeilen);
	const raus: Wechselbereich[] = [];
	let offen: number[] = [];

	zeilen.forEach((_z, i) => {
		const lage = lagen[i];
		if (lage === 'anfang') offen = [i];
		else if (lage === 'mitte' && offen.length) offen.push(i);
		else if (lage === 'ende' && offen.length) {
			offen.push(i);
			const bereich = ausIndizes(zeilen, offen);
			if (bereich) raus.push(bereich);
			offen = [];
		} else if (lage === 'keine') offen = [];
	});
	return raus;
}

function ausIndizes(zeilen: SubstratZeile[], indizes: number[]): Wechselbereich | null {
	const erste = indizes[0];
	const letzte = indizes[indizes.length - 1];
	const von = erste > 0 ? zahl(zeilen[erste - 1].tiefe) : 0;
	const bis = zahl(zeilen[letzte].tiefe);
	if (!(bis > von)) return null;

	// Gleiche Substrate in der Kette zusammenfassen: Sie sind dasselbe Band,
	// auch wenn sie an zwei Stellen der Kette stehen.
	const summen = new Map<string, number>();
	indizes.forEach(i => {
		const z = zeilen[i];
		const obergrenze = i > 0 ? zahl(zeilen[i - 1].tiefe) : 0;
		const dicke = Math.max(0, zahl(z.tiefe) - obergrenze);
		if (!z.substrat || dicke <= 0) return;
		summen.set(z.substrat, (summen.get(z.substrat) ?? 0) + dicke);
	});
	if (summen.size < 2) return null;    // ein Substrat allein wechselt mit nichts

	const gesamt = [...summen.values()].reduce((a, b) => a + b, 0);
	if (!(gesamt > 0)) return null;

	const lagen: WechselLage2[] = [...summen.entries()].map(([substrat, maechtigkeit]) => ({
		substrat,
		maechtigkeit,
		anteil: maechtigkeit / gesamt,
		korn: bodenart(substrat)?.korn ?? 0,
	}));

	return {
		von, bis, lagen,
		duennste: Math.min(...lagen.map(l => l.maechtigkeit)),
	};
}

/** Der Wechselbereich, der eine bestimmte Tiefe ueberdeckt. */
export function bereichBei(bereiche: Wechselbereich[], tiefeCm: number): Wechselbereich | null {
	return bereiche.find(b => tiefeCm > b.von && tiefeCm <= b.bis) ?? null;
}

/**
 * So dick wird EIN Band im Bild, in Zentimetern.
 *
 * Erfasst ist je Substrat nur die Summe seiner Lagen. Auf wie viele Baender sie
 * sich verteilt, steht nirgends - deshalb wird die duennste erfasste Lage so oft
 * geteilt, dass ein Band in der Groessenordnung liegt, in der Baender im
 * Gelaende auftreten (wenige Zentimeter). Mehr als [MAX_ZYKLEN] Durchgaenge
 * werden nicht gezeichnet: Was darunter liegt, waere im Maszstab ohnehin nur
 * noch ein Strich, und das Bild wuerde eine Genauigkeit vortaeuschen, die die
 * Daten nicht haben.
 */
export const BAND_ZIEL_CM = 3;
export const MAX_ZYKLEN = 6;

export function zyklen(bereich: Wechselbereich): number {
	if (!(bereich.duennste > 0)) return 1;
	return Math.max(1, Math.min(MAX_ZYKLEN, Math.round(bereich.duennste / BAND_ZIEL_CM)));
}

/**
 * Die Lage mit dem groessten Anteil - sie gilt im Bild als Grundmasse, die
 * uebrigen als die Baender darin.
 *
 * Welches Substrat wirklich die Baender bildet, sagen die Daten nicht; die
 * Mehrheit als Grundmasse zu nehmen ist eine Darstellungsentscheidung
 * (21.09.2026). Sie wird gebraucht, damit ein Bb↔C hell bleiben kann: Zieht
 * man JEDE Lage als Band nach, ist die ganze Flaeche uebermalt, und aus
 * „wenigen dunklen Baendern" wird ein dunkler Horizont.
 */
export function grundmasse(bereich: Wechselbereich): WechselLage2 | null {
	return bereich.lagen.reduce<WechselLage2 | null>(
		(beste, l) => (!beste || l.anteil > beste.anteil ? l : beste), null);
}

/**
 * Die Baender eines Wechselbereichs, von oben nach unten, als Anteile der
 * Gesamthoehe (0 bis 1). Jeder Zyklus zeigt alle beteiligten Substrate einmal,
 * jedes mit seinem Mengenanteil.
 *
 * `dichte` (0 bis 1, aus der Baenderstufe des Horizontes) duennt die Zyklen
 * aus: Ein Bb↔C bekommt weniger Durchgaenge als ein Bb. Die Mengenanteile
 * bleiben dabei, wie die Daten sie sagen.
 */
export function baenderFolge(
	bereich: Wechselbereich, dichte = 1,
): { lage: WechselLage2; von: number; bis: number }[] {
	const n = Math.max(1, Math.round(zyklen(bereich) * dichte));
	const raus: { lage: WechselLage2; von: number; bis: number }[] = [];
	let y = 0;
	for (let z = 0; z < n; z++) {
		for (const lage of bereich.lagen) {
			const hoehe = lage.anteil / n;
			raus.push({ lage, von: y, bis: y + hoehe });
			y += hoehe;
		}
	}
	return raus;
}

/**
 * Die Baender, die in einen bestimmten Tiefenabschnitt fallen, in cm.
 *
 * Gezeichnet wird je Abschnitt (die Abschnitte sind nach allen Grenzen beider
 * Folgen geteilt), die Baenderfolge gilt aber fuer den ganzen Wechselbereich.
 * Hier wird der passende Ausschnitt herausgeschnitten, damit die Baender ueber
 * Abschnittsgrenzen hinweg durchlaufen statt in jedem Abschnitt neu anzufangen.
 */
export function baenderImAbschnitt(
	bereich: Wechselbereich, vonCm: number, bisCm: number, dichte = 1,
): { lage: WechselLage2; von: number; bis: number }[] {
	const hoehe = bereich.bis - bereich.von;
	if (!(hoehe > 0)) return [];
	return baenderFolge(bereich, dichte)
		.map(b => ({
			lage: b.lage,
			von: bereich.von + b.von * hoehe,
			bis: bereich.von + b.bis * hoehe,
		}))
		.filter(b => b.bis > vonCm && b.von < bisCm)
		.map(b => ({
			lage: b.lage,
			von: Math.max(b.von, vonCm),
			bis: Math.min(b.bis, bisCm),
		}));
}

function zahl(v: unknown): number {
	return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}
